import type { PaymentInvoiceCompetenceItemDTO } from '../types/vida/payment/paymentType';

/** Competence window used in the initial load of the Payments tab. */
export const PAYMENT_TAB_COMPETENCIA_MESES = 4;

export interface CompetenciaDropdownOption {
  label: string;
  value: string;
}

export function competenciaToDropdownLabel(competenciaListaFormat: string): string {
  const m = /^(\d{2})-(\d{4})$/.exec(competenciaListaFormat.trim());
  if (!m) return competenciaListaFormat;
  return `${m[1]}/${m[2]}`;
}

export function parseCompetenciaMmYyyySortKey(competenciaListaFormat: string): number {
  const m = /^(\d{2})-(\d{4})$/.exec(competenciaListaFormat.trim());
  if (!m) return -1;
  return parseInt(m[2], 10) * 12 + parseInt(m[1], 10);
}

/** Dropdown options from the API items (all or filtered by subestipulante). */
export function buildCompetenciaDropdownOptions(
  items: PaymentInvoiceCompetenceItemDTO[],
  identificadorSubestipulante?: number
): CompetenciaDropdownOption[] {
  const filtered =
    identificadorSubestipulante != null
      ? items.filter((item) => item.identificadorSubestipulante === identificadorSubestipulante)
      : items;

  const seen = new Set<string>();
  const options: CompetenciaDropdownOption[] = [];

  for (const item of filtered) {
    const listaFormat = competenciaToListaFaturasFormat(item.competenciaFatura ?? '');
    if (!listaFormat || seen.has(listaFormat)) continue;
    seen.add(listaFormat);
    options.push({
      value: listaFormat,
      label: competenciaToDropdownLabel(listaFormat),
    });
  }

  options.sort((a, b) => parseCompetenciaMmYyyySortKey(b.value) - parseCompetenciaMmYyyySortKey(a.value));
  return options;
}

export function parseCompetenciaFatura(competencia: string): { year: number; month: number } | null {
  const trimmed = competencia.trim();
  const yyyyMm = /^(\d{4})-(\d{2})$/.exec(trimmed);
  if (yyyyMm) {
    const year = parseInt(yyyyMm[1], 10);
    const month = parseInt(yyyyMm[2], 10);
    if (month >= 1 && month <= 12) return { year, month };
  }
  const mmYyyy = /^(\d{2})-(\d{4})$/.exec(trimmed);
  if (mmYyyy) {
    const month = parseInt(mmYyyy[1], 10);
    const year = parseInt(mmYyyy[2], 10);
    if (month >= 1 && month <= 12) return { year, month };
  }
  return null;
}

export function competenciaToSortKey(year: number, month: number): number {
  return year * 12 + month;
}

export function getCompetenciaWindowKeys(
  totalMeses: number,
  referenceDate: Date = new Date()
): { startKey: number; endKey: number } {
  const base = new Date(referenceDate);
  base.setDate(1);
  base.setHours(0, 0, 0, 0);
  const endKey = competenciaToSortKey(base.getFullYear(), base.getMonth() + 1);
  const start = new Date(base);
  start.setMonth(start.getMonth() - (totalMeses - 1));
  const startKey = competenciaToSortKey(start.getFullYear(), start.getMonth() + 1);
  return { startKey, endKey };
}

export function isCompetenciaWithinUltimosMeses(
  competencia: string,
  totalMeses: number,
  referenceDate?: Date
): boolean {
  const parsed = parseCompetenciaFatura(competencia);
  if (!parsed) return false;
  const key = competenciaToSortKey(parsed.year, parsed.month);
  const { startKey, endKey } = getCompetenciaWindowKeys(totalMeses, referenceDate);
  return key >= startKey && key <= endKey;
}

/** Convert yyyy-MM (API) to MM-yyyy (lista-faturas). Keep MM-yyyy unchanged. */
export function competenciaToListaFaturasFormat(competencia: string): string {
  const trimmed = competencia.trim();
  const parsed = parseCompetenciaFatura(trimmed);
  if (!parsed) return trimmed;
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return `${String(parsed.month).padStart(2, '0')}-${parsed.year}`;
  }
  return trimmed;
}

/** Filter existing competences in the API within the last N months (lista-faturas format). */
export function filterCompetenciasUltimosMeses(
  competencias: string[],
  totalMeses: number,
  referenceDate?: Date
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const competencia of competencias) {
    if (!isCompetenciaWithinUltimosMeses(competencia, totalMeses, referenceDate)) continue;
    const listaFormat = competenciaToListaFaturasFormat(competencia);
    if (!listaFormat || seen.has(listaFormat)) continue;
    seen.add(listaFormat);
    result.push(listaFormat);
  }
  return result;
}
